import {
  BizNestClientError,
  BizNestClientValidationError,
  DatabaseAuto,
  IbulkDelete,
  IbulkDeleteSuccessResponse,
  IcompanySetting,
  IdataArrayResponse,
  Ifile, IfilterProps,
  TcompanySettingRequest,
  validateData
} from '@biz-nest/core-client';
import { lastValueFrom } from 'rxjs';
import { CommerceClient } from '../../../commerce-client';

/**
 * Represents company settings including document, tax, bank, and notification configurations.
 * Extends the base `DatabaseAuto` class to inherit common database operations.
 * @public
 * @remarks
 * This is the raw DTO shape as returned by the API. Prefer using the
 * {@link CompanySettings} class when invoking client operations.
 *
 * @usage
 * ```typescript
 * const companySettings = await CompanySettings.getAll();
 *
 * const companySettings = await CompanySettings.filterAll({
 *
 * });
 *
 * const companySettings = await CompanySettings.bulkDelete({
 *
 * });
 *
 * const companySettings = await CompanySettings.getOne('resourceId');
 *
 * const companySettings = await CompanySettings.remove();
 *
 * ```
 */
export class CompanySettings extends DatabaseAuto {
  /** Base API endpoint for company settings */
  private static readonly baseUrl = '/company-settings';

  /** Document-related settings */
  readonly documentSettings: IcompanySetting['documentSettings'];

  /** Tax-related settings including tax rates and configurations */
  readonly taxSettings: IcompanySetting['taxSettings'];

  /** Bank account and related financial settings */
  readonly bankSettings: IcompanySetting['bankSettings'];

  /** Settings related to document printing */
  readonly printDetails: IcompanySetting['printDetails'];

  /** Notification preferences and settings */
  readonly notifications: IcompanySetting['notifications'];

  /**
   * Creates a new instance of CompanySettings with validated data.
   * @param {IcompanySetting} data - The company settings data to initialize the instance with
   * @throws {BizNestClientValidationError} When validation of the input data fails
   */
  constructor(data: IcompanySetting) {
    // Validate input data
    validateData(data, {
      taxSettings: {
        required: false,
        validate: (v) => {
          if (!v) {
            return true;
          }

          // Taxes array validation
          if (!Array.isArray(v.taxes)) {
            throw new BizNestClientValidationError('Taxes must be an array');
          }

          v.taxes.forEach((tax, index) => {
            // Tax ID validation
            if (!tax.id || typeof tax.id !== 'string') {
              throw new BizNestClientValidationError(`Tax at index ${index} must have a valid ID`);
            }

            // Tax name validation
            if (!tax.name || typeof tax.name !== 'string') {
              throw new BizNestClientValidationError(`Tax at index ${index} must have a valid name`);
            }

            // Tax percentage validation
            if (typeof tax.percentage !== 'number' || tax.percentage < 0 || tax.percentage > 100) {
              throw new BizNestClientValidationError(`Tax percentage at 
                index ${index} must be a number between 0 and 100`);
            }

            // Tax status validation
            if (!['active', 'inactive'].includes(tax.status)) {
              throw new BizNestClientValidationError(`Tax status at index ${index} must be 'active' or 'inactive'`);
            }
          });

          return true;
        },
        message: 'Invalid tax settings'
      }
    });

    super(data);

    this.documentSettings = data.documentSettings;
    this.taxSettings = data.taxSettings;
    this.bankSettings = data.bankSettings;
    this.printDetails = data.printDetails;
    this.notifications = data.notifications;
  }

  /**
   * Retrieves a paginated list of company settings.
   * @param {number} [offset=0] - The number of records to skip (for pagination)
   * @param {number} [limit=20] - The maximum number of records to return
   * @returns {Promise<{count: number, dataArr: CompanySettings[]}>}
   * An object containing the total count and array of settings
   */
  static async getAll(offset = 0, limit = 20) {
    const query = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString()
    }).toString();
    const observer$ = CommerceClient.ehttp
      .makeGet<IdataArrayResponse<IcompanySetting>>(`${CompanySettings.baseUrl}?${query}`);
    const { count, data } = await lastValueFrom(observer$);

    return {
      count,
      dataArr: data.map((val) => new CompanySettings(val))
    };
  }

  /**
   * Filters company settings based on provided criteria.
   * @param {IfilterProps} filter - The filter criteria to apply
   * @returns {Promise<{count: number, dataArr: CompanySettings[]}>} Filtered results with count
   */
  static async filterAll(filter: IfilterProps) {
    const observer$ = CommerceClient.ehttp.makePost<IdataArrayResponse<IcompanySetting>>(
      `${CompanySettings.baseUrl}/filter`,
      filter
    );
    const { count, data } = await lastValueFrom(observer$);

    return {
      count,
      dataArr: data.map((val) => new CompanySettings(val))
    };
  }

  /**
   * Retrieves a single company setting by its ID.
   * @param {string} resourceId - The unique identifier of the setting to retrieve
   * @returns {Promise<CompanySettings>} The requested company settings
   * @throws {BizNestClientError} When the resource is not found
   */
  static async getOne(resourceId: string): Promise<CompanySettings> {
    const observer$ = CommerceClient.ehttp
      .makeGet<IcompanySetting>(`${CompanySettings.baseUrl}/${resourceId}`);
    const companySetting = await lastValueFrom(observer$);

    return new CompanySettings(companySetting);
  }

  /**
   * Creates or updates company settings.
   * @template T - Boolean flag indicating if files are included
   * @param {TcompanySettingRequest<T>} payload - The settings data to save
   * @returns {Promise<CompanySettings>} The saved company settings
   */
  static async addUpdate(payload: TcompanySettingRequest<true>) {
    const observer$ = CommerceClient.ehttp
      .makePost<IcompanySetting>(`${CompanySettings.baseUrl}`, payload);

    return new CompanySettings(await lastValueFrom(observer$));
  }

  /**
   * Uploads a signature file for the company.
   * @param {Ifile} file - The signature file to upload
   * @returns {Promise<IcompanySetting>} The updated company settings with signature
   * @throws {BizNestClientError} When no file is provided
   */
  static uploadSignature(file: Ifile) {
    if (!file || !file.blob) {
      throw new BizNestClientError('no file, please provide one');
    }

    return CommerceClient.ehttp.uploadFiles<IcompanySetting>(
      [file],
      `${CompanySettings.baseUrl}/upload-signature`
    );
  }

  /**
   * Uploads a stamp file for the company.
   * @param {Ifile} file - The stamp file to upload
   * @returns {Promise<IcompanySetting>} The updated company settings with stamp
   * @throws {BizNestClientError} When no file is provided
   */
  static uploadStamp(file: Ifile) {
    if (!file || !file.blob) {
      throw new BizNestClientError('no file, please provide one');
    }

    return CommerceClient.ehttp.uploadFiles<IcompanySetting>(
      [file],
      `${CompanySettings.baseUrl}/upload-stamp`
    );
  }

  /**
   * Deletes multiple company settings in bulk.
   * @param {IbulkDelete} payload - The criteria for bulk deletion
   * @returns {Promise<IbulkDeleteSuccessResponse>} The result of the bulk delete operation
   */
  static bulkDelete(payload: IbulkDelete) {
    const observer$ = CommerceClient.ehttp
      .makePost<IbulkDeleteSuccessResponse>(`${CompanySettings.baseUrl}/bulk-delete`, payload);

    return lastValueFrom(observer$);
  }

  /**
   * Removes specific images (signature/stamp) associated with the company settings.
   * @param {string[]} fileIds - Array of file IDs to remove
   * @returns {Promise<CompanySettings>} The updated company settings
   */
  async deleteImages(fileIds: string[]) {
    const observer$ = CommerceClient.ehttp.makePost<IcompanySetting>(
      `${CompanySettings.baseUrl}/delete-images`,
      { fileIds, _id: this._id }
    );

    return new CompanySettings(await lastValueFrom(observer$));
  }
}
